import React, { memo, useState, useEffect, useRef } from 'react';
import { Deal } from '../../lib/types';
import { Calendar, FileText, DollarSign, Clock, User, ArrowUpCircle, ChevronDown, CheckCircle, XCircle, Edit, AlertCircle, Loader2, X } from 'lucide-react';
import StatusChangeModal from './StatusChangeModal';
import { DealWhatsAppButton } from './DealWhatsAppButton';
import { useDealStore } from '../../lib/stores/dealStore';
import { useSettingsStore } from '../../lib/stores/settingsStore';
import { calculateDisplayAmount } from '../../lib/utils/dealAmountUtils';

interface DealClientInfoProps {
  deal: Deal;
  clientName?: string;
  translations: any;
  onStatusUpdate: () => void;
  onStatusChange?: (status: string, note?: string) => Promise<void>;
  onEditSignedDate?: (date: string) => void;
  onClientClick?: () => void;
  /**
   * Se true, l'header e' renderizzato dentro un drawer dove la X di
   * chiusura sta in `absolute top-3 left-2`. Aggiunge padding-left per
   * non far sovrapporre la X al titolo.
   */
  isDrawer?: boolean;
}

export const DealClientInfo: React.FC<DealClientInfoProps> = memo(({
  deal,
  clientName = '',
  translations: t,
  onStatusUpdate,
  onStatusChange,
  onEditSignedDate,
  onClientClick,
  isDrawer = false
}) => {
  const { statusUpdateLoading } = useDealStore();
  const { settings } = useSettingsStore();
  const noteRequired = settings?.deal_status_note_required ?? true;
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState('');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showDateModal, setShowDateModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowStatusDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Add page exit protection during status updates
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (statusUpdateLoading) {
        const message = 'Aggiornamento in corso. Sei sicuro di voler uscire?';
        event.preventDefault();
        event.returnValue = message;
        return message;
      }
    };

    if (statusUpdateLoading) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [statusUpdateLoading]);

  // Notion-style status colors
  const statusOptions = [
    { value: 'draft', label: 'Bozza', icon: Edit, color: 'bg-[rgba(55,53,47,0.08)] dark:bg-gray-700 text-[#787774] dark:text-gray-300', dotColor: '#787774' },
    { value: 'sent', label: 'Inviata', icon: FileText, color: 'bg-[rgba(35,131,226,0.1)] dark:bg-blue-900/30 text-[#2383E2] dark:text-blue-300', dotColor: '#2383E2' },
    { value: 'accepted', label: 'Accettata', icon: CheckCircle, color: 'bg-[rgba(15,123,108,0.1)] dark:bg-green-900/30 text-[#0F7B6C] dark:text-green-300', dotColor: '#0F7B6C' },
    { value: 'rejected', label: 'Rifiutata', icon: XCircle, color: 'bg-[rgba(212,76,71,0.1)] dark:bg-red-900/30 text-[#D44C47] dark:text-red-300', dotColor: '#D44C47' },
  ];

  const handleStatusChangeRequest = async (newStatus: string) => {
    if (newStatus === deal.status) return;
    
    setShowStatusDropdown(false);
    setPendingStatus(newStatus);
    setShowStatusModal(true);
  };

  const handleConfirmStatusChange = async (status: string, note: string) => {
    if (onStatusChange) {
      await onStatusChange(status, note);
    }
    setShowStatusModal(false);
    setPendingStatus('');
  };

  const handleDateEdit = () => {
    if (!onEditSignedDate) return;
    
    const currentDate = deal.signed_at 
      ? new Date(deal.signed_at) 
      : new Date();
    
    const dateStr = currentDate.toISOString().split('T')[0];
    setSelectedDate(dateStr);
    setShowDateModal(true);
  };

  const handleDateSave = () => {
    if (!selectedDate || !onEditSignedDate) {
      return;
    }

    try {
      const newDate = new Date(selectedDate);
      
      if (isNaN(newDate.getTime())) {
        throw new Error('Invalid date format');
      }
      
      onEditSignedDate(newDate.toISOString());
      setShowDateModal(false);
    } catch (error) {
      console.error('Invalid date format');
    }
  };

  // Get status badge with translation for current language
  const getStatusBadge = (status: string) => {
    // Show loading spinner if status is being updated
    if (statusUpdateLoading) {
      return (
        <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
          <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
          Aggiornamento in corso...
        </div>
      );
    }

    // Ensure translation exists for the status
    const statusText = t.statusBadge?.[status as keyof typeof t.statusBadge] || status;
    const iconMap = {
      'draft': () => null,
      'sent': () => null,
      'accepted': () => <ArrowUpCircle className="mr-1 h-3.5 w-3.5" />,
      'rejected': () => null
    };
    const StatusIcon = status in iconMap ? iconMap[status as keyof typeof iconMap] : () => null;

    switch (status) {
      case 'draft':
        return (
          <button
            onClick={onStatusUpdate}
            className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-700/30 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-200 transition-colors"
            title={t.updateStatus || "Aggiorna stato"}
          >
            {StatusIcon && <StatusIcon />}
            {statusText}
          </button>
        );
      case 'sent':
        return (
          <button
            onClick={onStatusUpdate}
            className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 hover:bg-blue-200 transition-colors"
            title={t.updateStatus || "Aggiorna stato"}
          >
            {StatusIcon && <StatusIcon />}
            {statusText}
          </button>
        );
      case 'accepted':
        return (
          <div className="inline-flex items-center gap-2">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
              {StatusIcon && <StatusIcon />}
              {statusText}
            </span>
            {deal.signed_at && (
              <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                <Calendar className="h-3 w-3" />
                <span>{formatDate(deal.signed_at)}</span>
                {onEditSignedDate && (
                  <button
                    onClick={handleDateEdit}
                    className="ml-0.5 p-0.5 hover:bg-green-200 dark:hover:bg-green-800 rounded transition-colors"
                    title="Modifica data firma"
                  >
                    <Edit className="h-2.5 w-2.5" />
                  </button>
                )}
              </div>
            )}
          </div>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
            {StatusIcon && <StatusIcon />}
            {statusText}
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-700/30 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
            {status}
          </span>
        );
    }
  };

  // Format date in a readable way
  const formatDate = (dateString?: string) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    return new Intl.DateTimeFormat('it-IT', options).format(date);
  };

  return (
    <div className={`${isDrawer ? 'pl-10 md:pl-12' : 'px-4 md:px-6'} pr-4 md:pr-6 py-4 md:py-5 flex flex-col gap-3`}>
      {/* Title - Notion large heading */}
      <div>
        <h1 className="text-xl md:text-2xl font-semibold text-[#37352F] dark:text-white leading-tight">{deal.title || 'Deal Details'}</h1>
      </div>

      {/* Properties block - Notion style */}
      <div className="flex flex-col gap-1.5">
        {/* Row 1: Amount + Status */}
        <div className="flex items-center gap-3 flex-wrap">
          {deal.deal_services && deal.deal_services.length > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-[#787774] dark:text-gray-500 w-16">Importo</span>
              {/* Importo con gradient Flusia (rosa → arancio), migliaia con
                  punto. Number() forzato perche' i campi numeric Postgres
                  arrivano spesso come stringa. */}
              <span
                className="text-sm font-semibold tabular-nums bg-clip-text text-transparent"
                style={{ backgroundImage: 'linear-gradient(135deg, #E91E8C, #F97316)' }}
              >
                €{Number(calculateDisplayAmount(deal)).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          )}
          
          {/* Status Dropdown - Notion style with dot indicator */}
          {onStatusChange ? (
            <div className="inline-flex items-center gap-2.5">
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => !statusUpdateLoading && setShowStatusDropdown(!showStatusDropdown)}
                  disabled={statusUpdateLoading}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                    statusUpdateLoading
                      ? 'bg-[rgba(35,131,226,0.1)] text-[#2383E2] dark:bg-blue-900/30 dark:text-blue-300 cursor-not-allowed'
                      : 'hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 cursor-pointer'
                  } ${!statusUpdateLoading ? (statusOptions.find(s => s.value === deal.status)?.color || 'bg-[rgba(55,53,47,0.08)] text-[#787774]') : ''}`}
                >
                  {statusUpdateLoading ? (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin" />
                      <span>Aggiornamento...</span>
                    </>
                  ) : (
                    <>
                      <div
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: statusOptions.find(s => s.value === deal.status)?.dotColor || '#787774' }}
                      />
                      <span>{statusOptions.find(s => s.value === deal.status)?.label || 'Sconosciuto'}</span>
                      <ChevronDown className={`h-3 w-3 transition-transform ${showStatusDropdown ? 'rotate-180' : ''}`} />
                    </>
                  )}
                </button>

                {showStatusDropdown && !statusUpdateLoading && (
                  <div className="absolute top-full right-0 mt-1 w-36 bg-white dark:bg-gray-800 border border-[rgba(55,53,47,0.09)] dark:border-gray-700 rounded-lg shadow-lg z-[100]">
                    <div className="py-1">
                      {statusOptions.map((status) => {
                        const isSelected = status.value === deal.status;

                        return (
                          <button
                            key={status.value}
                            onClick={() => handleStatusChangeRequest(status.value)}
                            disabled={isSelected}
                            className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left transition-colors text-[#37352F] dark:text-white ${
                              isSelected
                                ? 'bg-[rgba(55,53,47,0.04)] dark:bg-white/5 cursor-default'
                                : 'hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 cursor-pointer'
                            }`}
                          >
                            <div
                              className="w-2 h-2 rounded-full flex-shrink-0"
                              style={{ backgroundColor: status.dotColor }}
                            />
                            <span className={isSelected ? 'font-medium' : ''}>
                              {status.label}
                            </span>
                            {isSelected && (
                              <CheckCircle className="h-3 w-3 ml-auto text-[#0F7B6C]" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Data firma per status accepted - Notion style */}
              {deal.status === 'accepted' && deal.signed_at && (
                <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs bg-[rgba(15,123,108,0.08)] text-[#0F7B6C] dark:bg-green-900/20 dark:text-green-300 border border-green-200/50 dark:border-green-700/30">
                  <Calendar className="h-3.5 w-3.5" />
                  <span>{formatDate(deal.signed_at)}</span>
                  {onEditSignedDate && (
                    <button
                      onClick={handleDateEdit}
                      className="ml-0.5 p-0.5 hover:bg-[rgba(15,123,108,0.15)] dark:hover:bg-green-800 rounded transition-colors"
                      title="Modifica data firma"
                    >
                      <Edit className="h-3 w-3" />
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : (
            getStatusBadge(deal.status)
          )}
        </div>

        {/* Row 2: Properties grid - Notion style */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1 text-sm">
          {/* Cliente */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-[#787774] dark:text-gray-500 w-16 flex-shrink-0">Cliente</span>
            <button
              className="text-[#37352F] dark:text-white hover:text-[#2383E2] dark:hover:text-blue-400 hover:underline truncate"
              onClick={() => onClientClick?.()}
            >
              {clientName || '...'}
            </button>
            <DealWhatsAppButton client={deal.client} deal={deal} />
          </div>

          {/* Assegnatario */}
          {deal.assignee && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-[#787774] dark:text-gray-500 w-16 flex-shrink-0">Assegnato</span>
              <span className="text-[#37352F] dark:text-white truncate">{deal.assignee.full_name || deal.assignee.email}</span>
            </div>
          )}

          {/* Data */}
          {deal.created_at && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-[#787774] dark:text-gray-500 w-16 flex-shrink-0">Data</span>
              <span className="text-[#787774] dark:text-gray-400">{formatDate(deal.created_at)}</span>
            </div>
          )}

          {/* Numero preventivo */}
          {deal.quote_number && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-[#787774] dark:text-gray-500 w-16 flex-shrink-0">N° Prev</span>
              <span className="text-[#787774] dark:text-gray-400">#{deal.quote_number}</span>
            </div>
          )}

          {/* Riferimento */}
          {deal.offer_reference && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-[#787774] dark:text-gray-500 w-16 flex-shrink-0">Rif.</span>
              <span className="text-[#787774] dark:text-gray-400">{deal.offer_reference}</span>
            </div>
          )}

          {/* Percorso */}
          {deal.offer_path && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-[#787774] dark:text-gray-500 w-16 flex-shrink-0">Percorso</span>
              <span className="text-[#787774] dark:text-gray-400">{deal.offer_path}</span>
            </div>
          )}
        </div>
      </div>

      {/* Status Change Modal */}
      <StatusChangeModal
        isOpen={showStatusModal}
        onClose={() => {
          setShowStatusModal(false);
          setPendingStatus('');
        }}
        onConfirm={handleConfirmStatusChange}
        currentStatus={deal.status}
        newStatus={pendingStatus}
        dealTitle={deal.title || 'Deal'}
        noteRequired={noteRequired}
      />

      {/* Date Edit Modal */}
      {showDateModal && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-30">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-black dark:text-white flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Modifica Data di Firma
              </h3>
              <button
                onClick={() => setShowDateModal(false)}
                className="p-1 hover:bg-gray-700/50 dark:hover:bg-gray-700 rounded"
              >
                <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-black dark:text-white mb-2">
                  Data di Firma
                </label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                  Seleziona la data in cui l'offerta è stata firmata
                </p>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowDateModal(false)}
                className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-700/30 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg font-medium transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleDateSave}
                className="flex-1 px-4 py-2 text-white bg-black dark:bg-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Salva
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default DealClientInfo;